/**
 * Debug menu button — appears in the header bar when debug mode is enabled.
 *
 * Styled in the debug green/black theme. Lists every debug panel with a
 * toggle; panels that require pose mode are disabled when not in pose mode.
 * Open/closed state is persisted via the store (localStorage).
 */

import { useRef } from "react";
import { useStore } from "../app/store.ts";
import type { DebugPanelId } from "../app/storeTypes.ts";
import { hasFaceSelection } from "../modeling/editSelection.ts";
import { Button, IconButton } from "./Button.tsx";
import {
  buildSelectedFaceDebugMesh,
  serializeMeshDebug,
} from "./meshDebugExport.ts";
import { Popover } from "./Popover.tsx";
import { useTransientFeedback } from "./shared.tsx";
import {
  COLOR_DEBUG_HIGHLIGHT_BG,
  COLOR_TEXT_FAINT,
  FONT_SIZE_CAPTION,
  FONT_SIZE_TINY,
  RADIUS_CONTROL,
  RADIUS_DOT,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Panel definitions
// ---------------------------------------------------------------------------

interface PanelDef {
  id: DebugPanelId;
  label: string;
  /** When true, the panel is only relevant in pose mode */
  requiresPoseMode?: boolean;
}

const PANELS: PanelDef[] = [
  { id: "console", label: "Console" },
  { id: "recorder", label: "Recorder" },
  { id: "memoryProbe", label: "Memory Probe" },
  { id: "debugOverlay", label: "Performance Overlay" },
  { id: "debugMaterial", label: "Material Test" },
  { id: "bevelDebug", label: "Bevel Debug" },
  { id: "topologyViz", label: "Topology" },
  { id: "boneInfo", label: "Bone Info", requiresPoseMode: true },
  { id: "jointAngles", label: "Joint Angles", requiresPoseMode: true },
  { id: "shapeKeyLayers", label: "Shape Key Layers", requiresPoseMode: true },
  {
    id: "correctiveShapeKeys",
    label: "Corrective Shape Keys",
    requiresPoseMode: true,
  },
  { id: "poseCapture", label: "Pose Capture", requiresPoseMode: true },
  { id: "shadingDebug", label: "Shading Debug", requiresPoseMode: true },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DebugMenu({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const panelOpen = useStore((s) => s.debugPanelOpen);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const poseMode = useStore((s) => s.poseMode);
  const editMode = useStore((s) => s.editMode);
  const sceneObjects = useStore((s) => s.sceneObjects);

  const [copySuccess, fireCopySuccess] = useTransientFeedback(1500);
  const [copyFail, fireCopyFail] = useTransientFeedback(1500);
  const copyLabel = copySuccess ? "Copied!" : copyFail ? "Failed" : null;

  const btnRef = useRef<HTMLButtonElement>(null);

  const hasSelection = !!editMode && hasFaceSelection(editMode);
  const copyDisabledReason = !editMode
    ? "edit mode"
    : !hasSelection
      ? "select faces"
      : null;

  function handleCopyMesh() {
    if (!editMode || !hasFaceSelection(editMode)) return;
    const obj = sceneObjects.get(editMode.objectId);
    if (!obj) return;
    const selection = buildSelectedFaceDebugMesh(obj, editMode.selectedFaces);
    if (!selection) return;
    const text = serializeMeshDebug(selection.mesh, {
      title: "Mesh Selection Export",
      sourceFaceIndices: selection.sourceFaceIndices,
    });
    navigator.clipboard.writeText(text).then(fireCopySuccess, fireCopyFail);
  }

  return (
    <div style={{ position: "relative", pointerEvents: "auto" }}>
      <IconButton
        ref={btnRef}
        onClick={onToggle}
        icon={<span style={{ fontSize: 9, letterSpacing: 0.5 }}>{"</>"}</span>}
        label="Debug Panels"
        size={32}
        style={{
          border: open
            ? "1.5px solid rgba(0,255,0,0.6)"
            : "1.5px solid transparent",
          background: open ? "rgba(0,255,0,0.1)" : "none",
          color: open ? "#0f0" : COLOR_TEXT_FAINT,
          fontFamily: "monospace",
          fontWeight: "bold",
          opacity: open ? 1 : 0.8,
          flexShrink: 0,
          transition:
            "border-color 0.15s ease, color 0.15s ease, background 0.15s ease",
        }}
      />

      <Popover
        anchorRef={btnRef}
        open={open}
        onClose={onToggle}
        style={{
          minWidth: 200,
          padding: "6px 0",
          background: "rgba(0,0,0,0.92)",
          border: "1px solid rgba(0,255,0,0.25)",
          borderRadius: RADIUS_CONTROL,
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
        }}
      >
        <div
          style={{
            padding: "4px 10px 2px",
            fontFamily: "monospace",
            fontSize: 9,
            color: "#0f0",
            fontWeight: "bold",
            letterSpacing: 1,
            textTransform: "uppercase",
            opacity: 0.8,
          }}
        >
          Debug Panels
        </div>
        {PANELS.map((panel) => {
          const isOpen = panelOpen[panel.id];
          const disabled = panel.requiresPoseMode && !poseMode;
          return (
            <DebugMenuRow
              key={panel.id}
              label={panel.label}
              checked={isOpen}
              disabled={disabled}
              onClick={() => {
                togglePanel(panel.id);
              }}
            />
          );
        })}

        {/* Separator */}
        <div
          style={{
            height: 1,
            background: "rgba(0,255,0,0.15)",
            margin: "4px 10px",
          }}
        />

        {/* Copy Mesh Selection action */}
        <Button
          data-popover-item
          onClick={handleCopyMesh}
          disabled={!hasSelection}
          variant="ghost"
          size="xs"
          fullWidth
          style={{
            gap: 8,
            padding: "6px 12px",
            background: "transparent",
            color: !hasSelection ? "#335533" : "#0f0",
            fontFamily: "monospace",
            fontSize: FONT_SIZE_CAPTION,
            justifyContent: "flex-start",
            opacity: !hasSelection ? 0.5 : 1,
          }}
          onMouseEnter={(e) => {
            if (hasSelection)
              e.currentTarget.style.background = COLOR_DEBUG_HIGHLIGHT_BG;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          <span
            style={{
              width: 14,
              flexShrink: 0,
              textAlign: "center",
              fontSize: FONT_SIZE_CAPTION,
            }}
          >
            {copyLabel === "Copied!" ? "\u2713" : "\u2398"}
          </span>
          <span>{copyLabel ?? "Copy Mesh Selection"}</span>
          {copyDisabledReason && (
            <span style={{ fontSize: 9, color: "#335533", marginLeft: "auto" }}>
              {copyDisabledReason}
            </span>
          )}
        </Button>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Menu row
// ---------------------------------------------------------------------------

function DebugMenuRow({
  label,
  checked,
  disabled,
  onClick,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      data-popover-item
      onClick={onClick}
      disabled={disabled}
      variant="ghost"
      size="xs"
      fullWidth
      style={{
        gap: 8,
        padding: "6px 12px",
        background: "transparent",
        color: disabled ? "#335533" : checked ? "#0f0" : "#0a0",
        fontFamily: "monospace",
        fontSize: FONT_SIZE_CAPTION,
        justifyContent: "flex-start",
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled)
          e.currentTarget.style.background = COLOR_DEBUG_HIGHLIGHT_BG;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: RADIUS_DOT,
          border: "1px solid " + (disabled ? "#335533" : "rgba(0,255,0,0.4)"),
          background:
            checked && !disabled ? "rgba(0,255,0,0.2)" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: FONT_SIZE_TINY,
          flexShrink: 0,
        }}
      >
        {checked && !disabled ? "✓" : ""}
      </span>
      <span>{label}</span>
      {disabled && (
        <span style={{ fontSize: 9, color: "#335533", marginLeft: "auto" }}>
          pose mode
        </span>
      )}
    </Button>
  );
}
